import classNames from 'classnames';
import {useCallback, useMemo, useState} from 'react';
import InputLabel from '../InputLabel';
import './styles.css';

const FileUpload = ({ labelProps, title, description, onChange, accept, maxFileSize }) => {
  const [dragOver, setDragOver] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [errors, setErrors] = useState(null);

  const titleDisplay = useMemo(() => {
    if (fileList.length > 0) {
      return fileList[0].name;
    }

    return title;
  }, [fileList, title]);

  const descriptionDisplay = useMemo(() => {
    if (fileList.length > 0) {
      const fileSize = fileList[0].size / 1024 / 1024;
      return `${fileSize.toFixed(2)}MB`;
    }

    return description;
  }, [description, fileList]);

  const validateFiles = useCallback((files) => {
    if (accept) {
      const acceptedTypes = accept.split(', ');
      for (let i = 0; i < files.length; i++) {
        if (!acceptedTypes.includes(files[i].type)) {
          setErrors('Invalid file type');
          return false;
        }
      }
    }

    if (maxFileSize) {
      const maxSize = maxFileSize * 1024 * 1024;

      for (let i = 0; i < files.length; i++) {
        if (files[i].size > maxSize) {
          setErrors('File size is too large');
          return false;
        }
      }
    }

    setErrors(null);

    return true;
  }, [accept, maxFileSize]);

  const onDragEnter = useCallback((e) => {
    e.preventDefault();

    setDragOver(true);
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();

    setDragOver(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();

    setDragOver(false);

    if (!validateFiles(e.dataTransfer.files)) {
      setFileList([]);
      return;
    }

    if (e.dataTransfer.files.length > 0) {
      setFileList(e.dataTransfer.files);
    }

    if (onChange) {
      onChange(e.dataTransfer.files);
    }
  }, [onChange, validateFiles]);

  const onInputChange = useCallback((e) => {
    if (!validateFiles(e.target.files)) {
      setFileList([]);
      return;
    }

    if (e.target.files.length > 0) {
      setFileList(e.target.files)
    } else {
      setFileList([]);
    }

    if (onChange) {
      onChange(e.target.files);
    }
  }, [onChange, validateFiles]);

  return (
    <div className="file-upload">
      <InputLabel {...labelProps}>
        <label
          className={classNames('file-upload__drop-area', {
            'is--drag-over': dragOver,
          })}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          <input type="file" accept={accept} className={'file-upload__input'} onChange={onInputChange}/>
          <svg width="56" height="56" fill="none">
            <path stroke="#EB5428" strokeMiterlimit="10" strokeWidth="2" d="M48.04 14.63V52.5H7.96v-49H36.9l11.13 11.13Z"/>
            <path stroke="#EB5428" strokeMiterlimit="10" strokeWidth="2"
                  d="M28 32.46a4.46 4.46 0 1 0 0-8.92 4.46 4.46 0 0 0 0 8.92Zm0 0a6.67 6.67 0 0 1 6.67 6.67v2.33H21.33v-2.33A6.67 6.67 0 0 1 28 32.46ZM48.04 14.63v2.24H34.67V3.5h2.24l11.13 11.13Z"
            />
          </svg>
          {(!!titleDisplay) && (
            <p className={'file-upload__title'}>{titleDisplay}</p>
          )}
          {!!descriptionDisplay && (
            <p className={'file-upload__description'}>{descriptionDisplay}</p>
          )}
          {!!errors && (
            <p className={'file-upload__error'}>{errors}</p>
          )}
        </label>
      </InputLabel>
    </div>
  );
};

export default FileUpload;
